import React, { useState, useEffect } from 'react';
import { getProducts } from '../lib/db';
import type { ProductInput } from '../lib/types';
import toast from 'react-hot-toast';

interface ProductFormProps {
  onSubmit: (data: ProductInput) => void;
  onClose: () => void;
}

export function ProductForm({ onSubmit, onClose }: ProductFormProps) {
  const [formData, setFormData] = useState<ProductInput>({
    clientNumber: '',
    category: '',
    note: '',
    price: ''
  });
  const [description, setDescription] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [exactMatch, setExactMatch] = useState(false);

  useEffect(() => {
    loadClientNumbers();
  }, []);

  const loadClientNumbers = async () => {
    try {
      const products = await getProducts('draft');
      const uniqueClients = [...new Set(products.map(p => p.client_number))];
      setSuggestions(uniqueClients);
    } catch (error) {
      console.error('Erreur lors du chargement des numéros clients:', error);
      toast.error('Erreur lors du chargement des suggestions');
    }
  };

  const handleClientNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setFormData(prev => ({ ...prev, clientNumber: value }));
      
      const matchingSuggestions = suggestions.filter(s => 
        s.startsWith(value) && s !== value
      );
      
      setShowSuggestions(value.length > 0 && matchingSuggestions.length > 0);
      setExactMatch(suggestions.includes(value));
    }
  };

  const selectSuggestion = (value: string) => {
    setFormData(prev => ({ ...prev, clientNumber: value }));
    setShowSuggestions(false);
    setExactMatch(true);
  };

  const handleCategoryClick = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      category: categoryId
    }));
    setDescription(prev => [...prev, categoryId]);
  };

  const categories = [
    { id: 'Haut', label: 'Haut', color: 'bg-gray-200' },
    { id: 'Pant', label: 'Pant', color: 'bg-cyan-200' },
    { id: 'Robe', label: 'Robe', color: 'bg-pink-200' },
    { id: 'Jeans', label: 'Jeans', color: 'bg-green-100' },
    { id: 'Jupe', label: 'Jupe', color: 'bg-yellow-200' },
    { id: 'Acces', label: 'Acces', color: 'bg-blue-100' },
    { id: 'Promo', label: 'Promo', color: 'bg-red-200' },
    { id: 'Alim', label: 'Alim', color: 'bg-yellow-100' },
    { id: 'Autre', label: 'Autre', color: 'bg-purple-200' },
    { id: 'ENS', label: 'ENS', color: 'bg-indigo-200' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.clientNumber.trim()) {
      toast.error('Veuillez entrer un numéro de client');
      return;
    }

    if (description.length === 0) {
      toast.error('Veuillez ajouter au moins une description');
      return;
    }

    // Validate price
    const price = parseFloat(formData.price);
    if (!formData.price || isNaN(price) || price <= 0) {
      toast.error('Veuillez entrer un prix valide');
      return;
    }

    try {
      const submitData = {
        ...formData,
        note: description.join('\n'),
        price: price.toString() // Ensure we're sending a valid number as string
      };
      await onSubmit(submitData);
      setFormData(prev => ({
        ...prev,
        category: '',
        note: '',
        price: ''
      }));
      setDescription([]);
      toast.success('Produit ajouté avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'ajout du produit:', error);
      toast.error('Erreur lors de l\'ajout du produit');
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const lines = e.target.value.split('\n').filter(line => line.trim());
    setDescription(lines);
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow valid number input with up to 2 decimal places
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      setFormData(prev => ({ ...prev, price: value }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Numéro de Client:
        </label>
        <input
          type="text"
          inputMode="numeric"
          pattern="\d*"
          value={formData.clientNumber}
          onChange={handleClientNumberChange}
          onBlur={() => {
            setTimeout(() => {
              setShowSuggestions(false);
            }, 200);
          }}
          className="w-full p-2 border rounded-md"
          required
        />
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-10 w-full bg-white border rounded-md mt-1 shadow-lg max-h-48 overflow-y-auto">
            {suggestions
              .filter(s => s.startsWith(formData.clientNumber) && s !== formData.clientNumber)
              .map((suggestion, index) => (
                <div
                  key={index}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => selectSuggestion(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Catégorie:
        </label>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => handleCategoryClick(cat.id)}
              className={`px-3 py-1 rounded-md ${cat.color} hover:opacity-80 transition-opacity`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description:
        </label>
        <div className="space-y-2">
          <textarea
            value={description.join('\n')}
            onChange={handleDescriptionChange}
            className="w-full p-2 border rounded-md min-h-[100px] bg-white"
            placeholder="Cliquez sur les catégories ou modifiez directement le texte ici"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Prix:
        </label>
        <input
          type="number"
          value={formData.price}
          onChange={handlePriceChange}
          className="w-full p-2 border rounded-md"
          step="0.01"
          min="0.01"
          required
          placeholder="0.00"
        />
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
        >
          Fermer
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        >
          Ajouter
        </button>
      </div>
    </form>
  );
}